import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { CheckIcon } from "@phosphor-icons/react/dist/ssr/Check";
import { CopyIcon } from "@phosphor-icons/react/dist/ssr/Copy";

import type { PlatformSetting } from "@/hooks/api/use-platform-settings";

import { getSecretStatus, READ_ONLY_KEYS, SETTING_HELP } from "./lib";

export interface SettingRowProps {
	item: PlatformSetting;
	draft: string | undefined;
	dirty: boolean;
	disabled: boolean;
	onChange: (key: string, value: string) => void;
	onClearOverride: (item: PlatformSetting) => void;
}

// One settings row: label + status chip + helper line on the left, input on
// the right. Read-only keys render their value with a copy button instead.
export function SettingRow({ item, draft, dirty, disabled, onChange, onClearOverride }: SettingRowProps): React.JSX.Element {
	const [copied, setCopied] = React.useState(false);
	const status = getSecretStatus(item);
	const readOnly = READ_ONLY_KEYS.has(item.key);
	const help = SETTING_HELP[item.key];

	const handleCopy = async () => {
		await navigator.clipboard.writeText(item.value ?? "");
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<Stack
			direction={{ xs: "column", md: "row" }}
			spacing={{ xs: 1.5, md: 3 }}
			sx={{ alignItems: { md: "flex-start" }, justifyContent: "space-between", py: 2.5 }}
		>
			<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
				<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
					<Typography variant="subtitle2">{item.label}</Typography>
					<Chip label={status.label} color={status.color} size="small" variant="outlined" />
					{dirty ? <Chip label="Edited" color="warning" size="small" variant="outlined" /> : null}
				</Stack>
				{help ? (
					<Typography color="text.secondary" variant="caption" sx={{ display: "block", mt: 0.5 }}>
						{help}
					</Typography>
				) : null}
			</Box>
			<Box sx={{ flexShrink: 0, width: { xs: "100%", md: 360 } }}>
				{readOnly ? (
					<Stack
						direction="row"
						spacing={1}
						sx={{
							alignItems: "center",
							bgcolor: "var(--mui-palette-background-level1)",
							border: "1px solid var(--mui-palette-divider)",
							borderRadius: 1,
							pl: 1.5,
							pr: 0.5,
							py: 0.5,
						}}
					>
						<Typography
							variant="body2"
							sx={{ flex: "1 1 auto", fontFamily: "monospace", minWidth: 0, overflowWrap: "anywhere" }}
						>
							{item.value ?? "Not set"}
						</Typography>
						<Tooltip title={copied ? "Copied" : "Copy"}>
							<span>
								<IconButton disabled={!item.value} onClick={handleCopy} size="small">
									{copied ? <CheckIcon size={18} /> : <CopyIcon size={18} />}
								</IconButton>
							</span>
						</Tooltip>
					</Stack>
				) : (
					<Stack spacing={0.5}>
						{item.isSecret ? (
							<OutlinedInput
								autoComplete="new-password"
								disabled={disabled}
								fullWidth
								onChange={(event) => onChange(item.key, event.target.value)}
								placeholder={item.isSet ? "••••••••  (leave blank to keep)" : "Enter value"}
								size="small"
								type="password"
								value={draft ?? ""}
							/>
						) : (
							<OutlinedInput
								disabled={disabled}
								fullWidth
								onChange={(event) => onChange(item.key, event.target.value)}
								placeholder="Enter value"
								size="small"
								value={draft ?? item.value ?? ""}
							/>
						)}
						{item.isSecret && item.source === "db" ? (
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button color="error" disabled={disabled} onClick={() => onClearOverride(item)} size="small">
									Clear override
								</Button>
							</Box>
						) : null}
					</Stack>
				)}
			</Box>
		</Stack>
	);
}
